/**
 * i18n Configuration Constants
 *
 * Central configuration for the internationalization system.
 * Modify these values to adjust i18n behavior across the application.
 */
import type { LanguageCode } from "./types";

/**
 * Default/fallback language when user's preferred language is unavailable
 */
export const DEFAULT_LANGUAGE: LanguageCode = "en";

/**
 * Reference language - the source language for translations
 * This language is always loaded from local files, never from Crowdin
 */
export const REFERENCE_LANGUAGE: LanguageCode = "en";

/**
 * Default namespaces loaded on application start
 * These namespaces are loaded immediately and available without lazy loading
 *
 * All app namespaces are loaded upfront to prevent a flash of raw translation
 * keys on reload. The files are tiny (< 1 KB each) and precached by the
 * service worker, so the overhead is negligible.
 */
export const DEFAULT_NAMESPACES: string[] = [
  "common",
  "config-editor",
  "device-management",
  "device-registration",
  "home",
  "login",
  "session",
  "setup",
];

/**
 * Default namespace used when no namespace is specified in t() calls
 */
export const DEFAULT_NAMESPACE = "common";

/**
 * Path pattern for loading translation files
 * {{lng}} is replaced with language code (e.g., 'en')
 * {{ns}} is replaced with namespace (e.g., 'common')
 */
export const LOAD_PATH = "/locales/{{lng}}/{{ns}}.json";

/**
 * Interpolation configuration
 * These settings control how variables are inserted into translations
 */
export const INTERPOLATION_CONFIG = {
  /** React already escapes values, so we disable escaping here */
  escapeValue: false,
  /** Separator for formatting functions (e.g., currency, date) */
  formatSeparator: ",",
} as const;

/**
 * React-i18next specific configuration
 */
export const REACT_CONFIG = {
  /**
   * Events that trigger re-render when language changes
   * - languageChanged: when user switches language
   * - editorSaved: for in-context editor integration (Crowdin)
   */
  bindI18n: "languageChanged editorSaved",
  /**
   * Re-render when namespaces are added/removed from the i18next store.
   * Without this, components that request a namespace after init won't
   * re-render when the namespace finishes loading.
   */
  bindI18nStore: "added removed",
  /**
   * Suspend the component tree until translations are loaded.
   * Prevents a flash of raw translation keys on initial render.
   */
  useSuspense: true,
  /**
   * Namespace separator for cross-namespace references
   * e.g., t('common:cancel') references 'cancel' key in 'common' namespace
   */
  nsMode: "default" as const,
} as const;

/**
 * Backend configuration options
 */
export const BACKEND_CONFIG = {
  /** Path to load translation files from */
  loadPath: LOAD_PATH,
  /**
   * Request options for fetching translations
   * cache: 'default' allows browser caching which is important for PWA offline support
   */
  requestOptions: {
    cache: "default" as RequestCache,
  },
} as const;

/**
 * Debug mode - enable for development to see i18n logs
 * Controlled via environment variable
 */
export const I18N_DEBUG = import.meta.env.DEV;

// =============================================================================
// CROWDIN CONFIGURATION (COMMENTED - Enable when Crowdin integration is needed)
// =============================================================================

/**
 * Crowdin OTA (Over-The-Air) configuration
 * Uncomment and configure when setting up Crowdin integration
 *
 * These values come from your Crowdin project:
 * 1. Go to Crowdin project settings
 * 2. Navigate to "Over-The-Air Content Delivery"
 * 3. Create a distribution if not exists
 * 4. Copy the distribution hash
 */

// export const CROWDIN_CONFIG = {
//   /** Distribution hash from Crowdin OTA settings */
//   distributionHash: 'YOUR_DISTRIBUTION_HASH',
//   /** Distribution name (for logging/debugging) */
//   distributionName: 'production',
//   /** Reference language that's loaded from local files */
//   referenceLng: REFERENCE_LANGUAGE,
// } as const;

/**
 * Whether to use Crowdin for non-reference languages
 * When enabled:
 * - Reference language (en) loads from local /locales files
 * - Other languages load from Crowdin CDN
 *
 * When disabled:
 * - All languages load from local /locales files
 */
// export const USE_CROWDIN = import.meta.env.PROD;
